import { displayName, formatWhen, statusLabel, vehicleTitle } from './format';

export const RESALE_WORKFLOW_STEPS = [
  {
    id: 'arrival',
    number: 1,
    title: 'Check-in',
    lede: 'Confirm the visit and review seller notes from the app.',
  },
  {
    id: 'details',
    number: 2,
    title: 'Seller & vehicle',
    lede: 'Details that appear on the certified buyer report.',
  },
  {
    id: 'inspection',
    number: 3,
    title: 'Inspection',
    lede: 'Score all nine domains and attach evidence photos.',
  },
  {
    id: 'costs',
    number: 4,
    title: 'Costs & summary',
    lede: 'Repairs, maintenance forecast, and inspector sign-off.',
  },
  {
    id: 'publish',
    number: 5,
    title: 'Finalize & share',
    lede: 'Lock the report, publish the buyer link, and optionally quote repairs for the seller.',
  },
];

export function isResaleBooking(booking) {
  const label = `${booking?.service_type || ''}`.toLowerCase();
  return label.includes('pre-purchase') || label.includes('resale');
}

export function isCheckedIn(booking) {
  if (!booking) return true;
  return ['checked_in', 'in_progress', 'completed'].includes(booking.status);
}

export function deskItemHref(item) {
  if (item.kind === 'booking') {
    if (item.report?.id) return `/resale-inspections/${item.report.id}`;
    return `/resale-inspections/booking/${item.booking.id}`;
  }
  return `/resale-inspections/${item.report.id}`;
}

export function buildResaleDesk(bookings, reports) {
  const resaleBookings = (bookings || []).filter(isResaleBooking);
  const reportByBooking = new Map(
    (reports || []).filter((row) => row.booking_id).map((row) => [row.booking_id, row])
  );
  const linkedBookingIds = new Set(reportByBooking.keys());

  const pending = [];

  for (const booking of resaleBookings) {
    if (booking.status === 'cancelled') continue;
    const report = reportByBooking.get(booking.id) || null;
    if (report?.status === 'finalized') continue;
    pending.push({ kind: 'booking', booking, report });
  }

  for (const report of reports || []) {
    if (report.status !== 'draft') continue;
    if (report.booking_id && linkedBookingIds.has(report.booking_id)) continue;
    pending.push({ kind: 'walkin', report });
  }

  pending.sort((a, b) => {
    const dateA = new Date(a.booking?.scheduled_at || a.report?.created_at || 0);
    const dateB = new Date(b.booking?.scheduled_at || b.report?.created_at || 0);
    return dateA - dateB;
  });

  const confirmed = (reports || [])
    .filter((row) => row.status === 'finalized')
    .sort(
      (a, b) =>
        new Date(b.finalized_at || b.created_at) - new Date(a.finalized_at || a.created_at)
    )
    .map((report) => ({ kind: 'finalized', report }));

  return { pending, confirmed };
}

export function pendingCardTitle(item) {
  if (item.kind === 'booking') {
    return vehicleTitle(item.booking.vehicles) || 'Pre-purchase inspection';
  }
  const vehicle = [item.report.brand, item.report.model, item.report.plate_number]
    .filter(Boolean)
    .join(' ');
  return vehicle || item.report.report_number || 'Walk-in draft';
}

export function pendingCardMeta(item) {
  if (item.kind === 'booking') {
    const booking = item.booking;
    const seller = displayName(booking.profiles) || 'Seller from app';
    const when = formatWhen(booking.scheduled_at || booking.created_at);
    return `${seller} · ${when} · ${statusLabel(booking.status)}`;
  }
  const seller = item.report.seller_name?.trim() || 'Walk-in draft';
  return `${seller} · ${formatWhen(item.report.created_at)}`;
}

export function pendingBadge(item) {
  if (item.kind === 'booking') {
    if (!isCheckedIn(item.booking)) return 'Scheduled';
    if (item.report) return 'In progress';
    return 'Checked in';
  }
  return 'Walk-in draft';
}

export function confirmedCardMeta(report) {
  const vehicle = [report.brand, report.model, report.plate_number].filter(Boolean).join(' · ');
  const seller = report.seller_name?.trim() || 'Seller';
  const parts = [seller];
  if (vehicle) parts.push(vehicle);
  parts.push(formatWhen(report.finalized_at || report.created_at));
  if (report.share_token && !report.share_revoked_at) parts.push('Link live');
  return parts.join(' · ');
}
